<!--
  Create / Edit waypoint wizard — a port of the app's `waypoint_sheet.dart`.
  One step at a time: [changeLocation (edit only) →] topLevel → per-type chain →
  photo → details → privacy. Selection steps auto-advance on tap; submit is the
  header ✓, which lights up once every mandatory step is satisfied.
-->
<div class="bottom-sheet-overlay" [class.bottom-sheet-overlay--edit]="isEdit" *ngIf="isOpen" (click)="close()">
  <div class="bottom-sheet wizard-sheet" (click)="$event.stopPropagation()">
    <div class="drag-handle"></div>

    <!-- Header: back/close (or delete on edit's first page) · title + step subtitle · submit ✓ -->
    <div class="wiz-header">
      <button *ngIf="isEdit && history.length === 0" type="button" class="wiz-circle-btn wiz-circle-btn--danger"
        aria-label="Delete waypoint" (click)="requestDelete()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
          stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
      <button *ngIf="!(isEdit && history.length === 0)" type="button" class="wiz-circle-btn"
        [attr.aria-label]="history.length === 0 ? 'Close' : 'Back'" (click)="goBack()">
        <svg *ngIf="history.length === 0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
        </svg>
        <svg *ngIf="history.length > 0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </button>

      <div class="wiz-header__titles">
        <h2 class="wiz-title">{{ isEdit ? 'Edit waypoint' : 'Create waypoint' }}</h2>
        <span class="wiz-subtitle">{{ stepSubtitle }}</span>
      </div>

      <button type="button" class="wiz-circle-btn wiz-circle-btn--submit" [appTourAnchor]="'waypoint.confirmTick'"
        [class.is-enabled]="canSubmit" [disabled]="!canSubmit" [attr.aria-label]="isEdit ? 'Save waypoint' : 'Create waypoint'"
        (click)="submit()">
        <span class="wiz-spinner" *ngIf="isCreatingWaypoint"></span>
        <svg *ngIf="!isCreatingWaypoint" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
      </button>
    </div>

    <!-- Step body -->
    <div class="wiz-body" [ngSwitch]="currentStep">

      <!-- EDIT-only leading step: current location with a tap-to-change map. -->
      <ng-container *ngSwitchCase="'changeLocation'">
        <div class="wiz-pad">
          <div class="wiz-coord-line">
            <img src="assets/home/shared/starttrip_logo.svg" alt="" />
            <span>{{ getFullCoordinates() }}</span>
          </div>
          <div class="wiz-location-tile" [class.is-disabled]="!coords" (click)="coords && openCoordPicker()">
            <img *ngIf="getStaticMapUrl()" class="wiz-location-tile__map" [src]="getStaticMapUrl()" alt="" />
            <div class="wiz-location-tile__pin">
              <ng-container *ngTemplateOutlet="pinArt"></ng-container>
            </div>
            <div class="wiz-location-tile__hint">Tap to change location</div>
          </div>
        </div>
      </ng-container>

      <!-- Top level: Alerts / Places / Events -->
      <ng-container *ngSwitchCase="'topLevel'">
        <div class="wiz-list" [appTourAnchor]="'waypoint.optionsList'">
          <button type="button" class="wiz-row" *ngFor="let option of topLevelOptions"
            [appTourAnchor]="option.anchor || null" (click)="selectTopLevel(option.id)">
            <span class="wiz-row__icon"><img [src]="option.icon" alt="" /></span>
            <span class="wiz-row__label">{{ option.label }}</span>
            <span class="wiz-row__check" [class.is-selected]="topLevelCategory === option.id">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </span>
          </button>
        </div>
      </ng-container>

      <!-- Events subtype: Fish Caught / Fish Mark / Bait Mark / Temp Break -->
      <ng-container *ngSwitchCase="'eventsSubtype'">
        <div class="wiz-list">
          <button type="button" class="wiz-row" *ngFor="let option of eventsSubtypeOptions"
            [appTourAnchor]="option.anchor || null" (click)="selectEventsSubtype(option.id)">
            <span class="wiz-row__icon"><img [src]="option.icon" alt="" /></span>
            <span class="wiz-row__label">{{ option.label }}</span>
            <span class="wiz-row__check" [class.is-selected]="eventsSubtype === option.id">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </span>
          </button>
        </div>
      </ng-container>

      <!-- Fish family: Tuna / Billfish / Pelagic / Inshore, then Others -->
      <ng-container *ngSwitchCase="'fishFamily'">
        <div class="wiz-list">
          <button type="button" class="wiz-row" *ngFor="let option of fishFamilyOptions"
            [appTourAnchor]="option.anchor || null" (click)="selectFishFamily(option.id)">
            <span class="wiz-row__icon wiz-row__icon--art">
              <img [src]="option.icon" alt="" (error)="onRowIconError($event, option.fallbackIcon || '')" />
            </span>
            <span class="wiz-row__label">{{ option.label }}</span>
            <span class="wiz-row__check" [class.is-selected]="fishFamily === option.id">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </span>
          </button>
        </div>
      </ng-container>

      <!-- Fish species for the chosen family ("Others" skips this step) -->
      <ng-container *ngSwitchCase="'fishSpecies'">
        <div class="wiz-list">
          <button type="button" class="wiz-row" *ngFor="let type of visibleFishTypes"
            [appTourAnchor]="type.id === 'yellowfin' ? 'waypoint.yellowfinOption' : null"
            (click)="selectFishSpecies(type)">
            <span class="wiz-row__icon wiz-row__icon--art">
              <img [src]="speciesRowIcon(type)" alt="" (error)="onRowIconError($event, type.icon)" />
            </span>
            <span class="wiz-row__label">{{ type.name === 'Other' ? 'Others' : type.name }}</span>
            <span class="wiz-row__check" [class.is-selected]="type.selected">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </span>
          </button>
        </div>
      </ng-container>

      <!-- Dedicated quantity step for Fish Caught -->
      <ng-container *ngSwitchCase="'fishQuantity'">
        <div class="wiz-pad">
          <div class="wiz-qty" [appTourAnchor]="'waypoint.quantityInput'" role="group" aria-label="Fish quantity">
            <button type="button" class="wiz-qty__btn" (click)="incrementFishQuantity(-1)"
              [disabled]="fishQuantity <= 1" aria-label="Decrease quantity">−</button>
            <input class="wiz-qty__input" type="text" inputmode="numeric" maxlength="2" [ngModel]="fishQuantity || 1"
              (ngModelChange)="onFishQuantityInput($event)" aria-label="Quantity" />
            <button type="button" class="wiz-qty__btn wiz-qty__btn--add" (click)="incrementFishQuantity(1)"
              [disabled]="fishQuantity >= 99" aria-label="Increase quantity">+</button>
          </div>
        </div>
      </ng-container>

      <!-- Places subtype: Spot / Wreck / Inlet / Anchorage / Home -->
      <ng-container *ngSwitchCase="'placesSubtype'">
        <div class="wiz-list">
          <button type="button" class="wiz-row" *ngFor="let type of placeTypes" (click)="selectPlacesSubtype(type)">
            <span class="wiz-row__icon"><img [src]="type.icon" alt="" /></span>
            <span class="wiz-row__label">{{ type.name }}</span>
            <span class="wiz-row__check" [class.is-selected]="type.selected">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </span>
          </button>
        </div>
      </ng-container>

      <!-- Alerts subtype: Hazard / Diver / Patrol / Fog -->
      <ng-container *ngSwitchCase="'alertsSubtype'">
        <div class="wiz-list">
          <button type="button" class="wiz-row" *ngFor="let type of alertTypes" (click)="selectAlertsSubtype(type)">
            <span class="wiz-row__icon"><img [src]="type.icon" alt="" /></span>
            <span class="wiz-row__label">{{ type.name }}</span>
            <span class="wiz-row__check" [class.is-selected]="type.selected">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </span>
          </button>
        </div>
      </ng-container>

      <!-- Patrol jurisdiction: State / Federal -->
      <ng-container *ngSwitchCase="'patrolJurisdiction'">
        <div class="wiz-list">
          <button type="button" class="wiz-row" *ngFor="let option of patrolJurisdictionOptions"
            (click)="selectPatrolJurisdiction(option.id)">
            <span class="wiz-row__icon"><img [src]="option.icon" alt="" /></span>
            <span class="wiz-row__label">{{ option.label }}</span>
            <span class="wiz-row__check" [class.is-selected]="patrolJurisdiction === option.id">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </span>
          </button>
        </div>
      </ng-container>

      <!-- Attachments -->
      <ng-container *ngSwitchCase="'photo'">
        <div class="wiz-pad">
          <div class="wiz-attach-rows">
            <button type="button" class="wiz-row wiz-row--action" [disabled]="!canAddMoreMedia"
              (click)="galleryInput.click()">
              <span class="wiz-row__icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                </svg>
              </span>
              <span class="wiz-row__label">Gallery</span>
            </button>
          </div>

          <div class="wiz-thumbs" *ngIf="totalImageCount > 0">
            <div class="wiz-thumb" *ngFor="let url of existingImageUrls; let i = index">
              <img [src]="url" alt="" />
              <button type="button" class="wiz-thumb__remove" aria-label="Remove photo"
                (click)="removeExistingMedia(i)">&times;</button>
            </div>
            <div class="wiz-thumb" *ngFor="let media of selectedMedia; let i = index">
              <img [src]="media.url" alt="" />
              <button type="button" class="wiz-thumb__remove" aria-label="Remove photo"
                (click)="removeMedia(i)">&times;</button>
            </div>
          </div>

          <!-- Gallery only: the app's "Capture Photo" row is a phone-camera
               affordance and doesn't apply on web. -->
          <input type="file" #galleryInput hidden multiple accept="image/*" (change)="onFileSelected($event)" />
        </div>
      </ng-container>

      <!-- Combined details: name (places/alerts) + caption, plus the spot/inlet toggle -->
      <ng-container *ngSwitchCase="'details'">
        <div class="wiz-pad">
          <div class="wiz-toggle-row" *ngIf="isSpotOrInletSelected()">
            <span class="wiz-toggle-row__label">Appear as icon</span>
            <button type="button" class="wiz-switch" [class.is-on]="showIcon" role="switch"
              [attr.aria-checked]="showIcon" (click)="showIcon = !showIcon">
              <span class="wiz-switch__knob"></span>
            </button>
          </div>

          <ng-container *ngIf="usesCustomTitle">
            <label class="wiz-field-label">Give this {{ selectedPrimaryType?.name }} a name (optional).</label>
            <input class="wiz-input" type="text" maxlength="30" placeholder="Name (optional)" [(ngModel)]="placeName" />
            <div class="wiz-counter" [class.is-near-limit]="placeName.length >= 25">{{ placeName.length }}/30</div>
          </ng-container>

          <label class="wiz-field-label">Add a caption (optional).</label>
          <textarea class="wiz-textarea" placeholder="Add Waypoint Details..." [(ngModel)]="description"></textarea>
        </div>
      </ng-container>

      <!-- Visibility (skipped entirely for alerts) -->
      <ng-container *ngSwitchCase="'privacy'">
        <div class="wiz-pad wiz-privacy">
          <label class="wiz-field-label">Who can view this waypoint?</label>

          <div class="privacy-options-list">
            <!-- The Water -->
            <!-- "The Water" is unavailable while a restrictive fleet is selected:
                 publishing it would defeat the restriction (the app's
                 `disabledByRestrictiveSharing` on privacy option 0). -->
            <div class="privacy-item" [class.disabled]="isEveryoneOptionDisabled"
              (click)="selectPrivacy('public')">
              <div class="item-left">
                <div class="icon-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                  </svg>
                </div>
                <div class="item-text">
                  <span class="main">The Water (Everyone)</span>
                  <span class="sub">Anyone else on Vector can view this waypoint in feed or search.</span>
                </div>
              </div>
              <div class="radio-circle" [class.selected]="visibility === 'public'"></div>
            </div>

            <!-- Private -->
            <div class="privacy-item" (click)="selectPrivacy('private')">
              <div class="item-left">
                <div class="icon-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                  </svg>
                </div>
                <div class="item-text">
                  <span class="main">Private (Only Me)</span>
                  <span class="sub">This waypoint is private, It's only for your purpose.</span>
                </div>
              </div>
              <div class="radio-circle" [class.selected]="visibility === 'private'"></div>
            </div>

            <!-- Pier Circle -->
            <div class="privacy-item" (click)="selectPrivacy('custom')">
              <div class="item-left">
                <div class="icon-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                  </svg>
                </div>
                <div class="item-text">
                  <span class="main">Pier Circle (Dockmates and Fleets)</span>
                  <span class="sub">Select specific followers or groups who can view this waypoint.</span>
                </div>
              </div>
              <div class="radio-circle" [class.selected]="visibility === 'custom'"></div>
            </div>
          </div>

          <!-- Pier Circle audience -->
          <div class="pier-circle-selection" *ngIf="visibility === 'custom'">
            <!-- Sealed to a restrictive fleet: say so, and show only that fleet.
                 The app replaces the whole picker in this state rather than
                 offering choices it will then refuse. -->
            <div class="restrictive-notice" *ngIf="hasRestrictedFleetRef">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="5" y="11" width="14" height="10" rx="2"></rect>
                <path d="M8 11V7.5a4 4 0 0 1 8 0V11"></path>
              </svg>
              <span>This waypoint belongs to a restrictive fleet and can only be shared with it.</span>
            </div>

            <div class="search-box" *ngIf="!hasRestrictedFleetRef">
              <img class="search-icon" src="assets/home/profile/searchicon.svg" />
              <input type="text" placeholder="Search Dockmates & ..." [(ngModel)]="visibilitySearchQuery" />
            </div>

            <div class="selection-section">
              <label>Fleets</label>
              <div class="item-list">
                <!-- A restrictive fleet can only ever be the SOLE recipient, so
                     every other row locks once one is picked (and vice versa) —
                     the same rule the Send sheet enforces. -->
                <div class="list-item" *ngFor="let fleet of filteredFleets"
                  [class.list-item--locked]="isPrivacyFleetLocked(fleet)">
                  <div class="item-info">
                    <div class="checkbox" [class.checked]="fleet.selected"
                      [class.disabled]="isPrivacyFleetLocked(fleet)" (click)="togglePrivacyFleet(fleet)">
                    </div>
                    <img *ngIf="fleet.image_url || fleet.image || fleet.logo_url"
                      [src]="fleet.image_url || fleet.image || fleet.logo_url" class="avatar" />
                    <div *ngIf="!fleet.image_url && !fleet.image && !fleet.logo_url" class="avatar-fallback">
                      {{ getInitials(fleet.name) }}
                    </div>
                    <div class="info-text">
                      <span class="name">{{ fleet.name }}</span>
                      <span class="meta" *ngIf="!isPrivacyFleetLocked(fleet)">
                        {{ fleet.members_count || 0 }} members
                        <ng-container *ngIf="fleet.isRestrictive"> · Restrictive fleet</ng-container>
                      </span>
                      <span class="meta meta--locked" *ngIf="isPrivacyFleetLocked(fleet)">
                        Locked by restrictive fleet
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="selection-section" *ngIf="!hasRestrictedFleetRef">
              <label>Pier Circle</label>
              <div class="item-list">
                <div class="list-item" *ngFor="let mate of filteredDockmates"
                  [class.list-item--locked]="isPrivacyDockmateLocked(mate)">
                  <div class="item-info">
                    <div class="checkbox" [class.checked]="mate.selected"
                      [class.disabled]="isPrivacyDockmateLocked(mate)" (click)="togglePrivacyDockmate(mate)">
                    </div>
                    <img *ngIf="mate.user?.image_url" [src]="mate.user?.image_url" class="avatar" />
                    <div *ngIf="!mate.user?.image_url" class="avatar-fallback">
                      {{ getInitials(mate.user?.name || mate.name) }}
                    </div>
                    <div class="info-text">
                      <span class="name">{{ mate.user?.name || mate.name }}</span>
                      <span class="meta" *ngIf="!isPrivacyDockmateLocked(mate)">{{ mate.vessel_name || 'No Vessel' }}</span>
                      <span class="meta meta--locked" *ngIf="isPrivacyDockmateLocked(mate)">
                        Locked by restrictive fleet
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Bottom: optional Skip (input steps only) over the mandatory-step progress bar -->
    <div class="wiz-bottom">
      <button *ngIf="stepHasBottomBar" type="button" class="wiz-skip-btn"
        [disabled]="!canAdvanceFromStep(currentStep) || isCreatingWaypoint" (click)="advanceFromStep(currentStep)">
        Skip
      </button>
      <div class="wiz-progress">
        <div class="wiz-progress__fill" [style.width.%]="progressFraction * 100"></div>
      </div>
    </div>
  </div>

  <!-- Delete confirmation (edit only) -->
  <div class="wiz-confirm-overlay" *ngIf="showDeleteConfirm" (click)="cancelDelete()">
    <div class="wiz-confirm" (click)="$event.stopPropagation()">
      <h3>Delete Waypoint</h3>
      <p>Are you sure you want to delete this waypoint? This action cannot be undone.</p>
      <div class="wiz-confirm__actions">
        <button type="button" class="wiz-confirm__btn" (click)="cancelDelete()">Cancel</button>
        <button type="button" class="wiz-confirm__btn wiz-confirm__btn--danger" [disabled]="isDeleting"
          (click)="confirmDelete()">{{ isDeleting ? 'Deleting…' : 'Delete' }}</button>
      </div>
    </div>
  </div>

  <div class="coord-picker-overlay" *ngIf="showCoordPicker">
    <div class="coord-picker-header">
      <button type="button" class="wiz-circle-btn" aria-label="Cancel coord picker" (click)="closeCoordPicker()">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </button>
      <div class="cp-coord-display">{{ pickedCoordsDisplay || 'Move map to pick location' }}</div>
      <button type="button" class="wiz-circle-btn wiz-circle-btn--submit is-enabled" aria-label="Confirm location"
        (click)="confirmCoordPicker()">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
      </button>
    </div>

    <div class="cp-hint">Move the map to select exact position</div>
    <div id="create-coord-picker-map" class="coord-picker-map"></div>

    <div class="cp-center-pin">
      <div class="cp-center-pin__shell">
        <ng-container *ngTemplateOutlet="pinArt"></ng-container>
      </div>
    </div>

    <div class="cp-zoom-controls">
      <button type="button" (click)="zoomInPicker()">+</button>
      <button type="button" (click)="zoomOutPicker()">−</button>
    </div>

    <button type="button" class="cp-location-btn" [class.is-locating]="locatingMe" [disabled]="locatingMe"
      (click)="useCurrentLocationPicker()">
      <span class="cp-loc-spinner" *ngIf="locatingMe"></span>
      <img *ngIf="!locatingMe" src="assets/home/shared/starttrip_logo.svg" />
      {{ locatingMe ? 'Locating…' : 'Use Current Location' }}
    </button>
  </div>
</div>

<!-- Shared map-pin art for the location tile and the coordinate picker. -->
<ng-template #pinArt>
  <span class="wp-pin">
    <img class="wp-pin__art" [src]="selectedTypeIcon" alt="" />
    <ng-container *ngIf="isSpotSelected">
      <i class="wp-pin__cover"></i>
      <img class="wp-pin__x" src="assets/waypoint_icons/spot_x_flutter.png" alt="" />
    </ng-container>
  </span>
</ng-template>
